'use client';

// React
import { createContext, useState, useCallback, useContext, useMemo, useEffect, useRef } from 'react';

// Libraries
import { DayPicker } from 'react-day-picker';
import type { DayButtonProps } from 'react-day-picker';
import { endOfDay, format, setMonth, setYear, startOfDay } from 'date-fns';

// UI primitives
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { FieldTrigger } from '@/components/ui/field-trigger';
import { PickerDropdown } from '@/components/ui/picker-dropdown';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Icons
import { CalendarIcon } from '@icons/CalendarIcon';

// Hooks and utilities
import { useTranslation } from '@hooks/useTranslation';
import { toLocalDateKey } from '@/react/utils/dateTime';
import { cn } from '@/lib/utils';

export interface CalendarDatePickerProps {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  disabledDaysOfWeek?: number[];
  minDate?: Date;
  maxDate?: Date;
  defaultMonth?: Date;
  fromYear?: number;
  toYear?: number;
  className?: string;
  disabled?: boolean;
  /** Paints the error (chilli-red) border to signal a validation error. */
  invalid?: boolean;
  /** Content rendered below the calendar grid, above the Today action row. */
  footer?: React.ReactNode;
  /** Controlled open state — when provided, the component is controlled */
  open?: boolean;
  /** Called when the popover open state changes */
  onOpenChange?: (open: boolean) => void;
  /**
   * When true, clicking the selected date does not deselect it — react-day-picker
   * suppresses the second-click `onSelect(undefined)`. Use for pages that always
   * need a selected date (e.g. /capacities) so the picker can't end up empty.
   */
  required?: boolean;
  /** Marked custard-yellow, with a tooltip when `label` is set. Still selectable. */
  closedDays?: Array<{ date: Date; label?: string }>;
  onMonthChange?: (month: Date) => void;
  /**
   * Override which date is treated as "today" for the dinnerplate-white
   * highlight. Defaults to the system date. Used in stories/snapshots where
   * the real today isn't on-screen.
   */
  today?: Date;
  /**
   * Shows a Clear action (only while a date is selected) to the left of Today,
   * for surfaces where an empty date is a valid end state (e.g. an optional
   * field or a list filter). Defaults to false so required-date surfaces are
   * unaffected.
   */
  showClear?: boolean;
}

export const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const formatWeekdayName = (date: Date) => WEEKDAYS[date.getDay()];
// Exported (with `calendarClassNames` below) so DateRangePicker shares the same
// day-grid formatting and styling — keep them in sync if either changes.
export const FORMATTERS = { formatWeekdayName };

// Stable empty default so `disabledDaysOfWeek`'s identity doesn't change between
// renders (which would re-run the disabled-matcher memos needlessly).
const EMPTY_DISABLED: number[] = [];

/* -------------------------------------------------------------------------- */
/*  CalendarPickerDropdown – back-compat alias of the canonical PickerDropdown */
/*  primitive. The month/year row uses this so the existing DateRangePicker     */
/*  import keeps working.                                                       */
/* -------------------------------------------------------------------------- */

export const CalendarPickerDropdown = PickerDropdown;

export const CALENDAR_WEEK_STARTS_ON = 0 as const;

const ClosedDayLabelsContext = createContext<Map<string, string> | undefined>(undefined);

// Same tree with or without a label, so days don't remount and lose focus.
function CalendarDayButton({ day, modifiers, ...props }: DayButtonProps) {
  const label = useContext(ClosedDayLabelsContext)?.get(toLocalDateKey(day.date));
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <Tooltip open={Boolean(label) && tooltipOpen} onOpenChange={setTooltipOpen}>
      <TooltipTrigger asChild>
        <button ref={ref} {...props} />
      </TooltipTrigger>
      {label && (
        <TooltipContent variant="dark" side="right" sideOffset={12} passthrough>
          {label}
        </TooltipContent>
      )}
    </Tooltip>
  );
}

const DAY_COMPONENTS = { DayButton: CalendarDayButton };

// Static classNames hoisted to module level to avoid re-creation
export const calendarClassNames = {
  months: 'flex flex-col items-center',
  month: 'w-full',
  month_caption: 'hidden',
  month_grid: 'w-full border-separate border-spacing-x-0 border-spacing-y-[18px] -my-[18px]',
  weekday: 'text-center text-[15px] font-semibold font-inter text-off-black py-0',
  day: 'group relative text-center text-[15px] font-normal font-inter p-0',
  day_button: cn(
    'cursor-pointer mx-auto flex size-[26.5px] items-center justify-center rounded-xl text-[15px] font-normal font-inter text-off-black transition-colors',
    'group-data-[today]:bg-dinnerplate-white-light',
    // `closed` is applied via `modifiersClassNames` (not group-data-*)
    // since react-day-picker v9 doesn't add data attributes for custom
    // modifiers. The day cell carries `.is-closed` and the `group`
    // helper class, so we react via `group-[.is-closed]:` here.
    'group-[.is-closed]:bg-custard-yellow',
    'group-[.is-closed]:hover:bg-butter-yellow',
    'hover:bg-dinnerplate-white-light',
    'group-data-[selected]:!bg-brand-yellow group-data-[selected]:font-semibold group-data-[selected]:text-off-black',
    'group-data-[disabled]:text-grey-light group-data-[disabled]:cursor-default group-data-[disabled]:hover:bg-transparent',
    'group-data-[outside]:text-grey-light group-data-[selected]:group-data-[outside]:text-off-black',
    'outline-none focus-visible:outline-none focus-visible:ring-0',
  ),
  hidden: 'invisible',
};

export function CalendarDatePicker({
  value,
  onChange,
  placeholder = 'Select date',
  disabledDaysOfWeek = EMPTY_DISABLED,
  minDate,
  maxDate,
  defaultMonth,
  fromYear = 2020,
  toYear = 2030,
  className,
  disabled = false,
  invalid = false,
  footer,
  open: controlledOpen,
  onOpenChange,
  required = false,
  closedDays,
  onMonthChange,
  today,
  showClear = false,
}: CalendarDatePickerProps) {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = useCallback(
    (value: boolean) => {
      if (!isControlled) setInternalOpen(value);
      onOpenChange?.(value);
    },
    [isControlled, onOpenChange],
  );
  const [month, setDisplayMonth] = useState<Date>(value || defaultMonth || new Date());

  useEffect(() => {
    if (value) {
      setDisplayMonth(value);
    } else if (defaultMonth) {
      setDisplayMonth(defaultMonth);
    }
  }, [defaultMonth, open, value]);

  // Keyed on year+month: the reopen effect above sets an equal-but-new Date.
  const onMonthChangeRef = useRef(onMonthChange);
  useEffect(() => {
    onMonthChangeRef.current = onMonthChange;
  }, [onMonthChange]);
  const shownYear = month.getFullYear();
  const shownMonthIndex = month.getMonth();
  useEffect(() => {
    onMonthChangeRef.current?.(new Date(shownYear, shownMonthIndex, 1));
  }, [shownYear, shownMonthIndex]);

  const handleSelect = useCallback(
    (date: Date | undefined) => {
      onChange?.(date);
      // Close only when a date was picked. On deselect (non-required mode,
      // react-day-picker passes `undefined`) we keep the popover open so the
      // user can immediately pick another date. The Today button always passes
      // a real date, so it always closes.
      if (date) setOpen(false);
    },
    [onChange, setOpen],
  );

  const handleMonthChange = useCallback((val: string) => {
    setDisplayMonth((prev) => setMonth(prev, parseInt(val)));
  }, []);

  const handleYearChange = useCallback((val: string) => {
    setDisplayMonth((prev) => setYear(prev, parseInt(val)));
  }, []);

  const displayValue = value ? format(value, 'dd MMM yyyy') : null;

  const monthItems = useMemo(
    () => MONTHS.map((name, i) => ({ label: name, value: String(i) })),
    [],
  );

  const yearItems = useMemo(
    () => Array.from({ length: toYear - fromYear + 1 }, (_, i) => ({
      label: String(fromYear + i),
      value: String(fromYear + i),
    })),
    [fromYear, toYear],
  );

  // Normalize bounds at the boundary so callers can pass any time-of-day
  // and the component treats min/max as day-level constraints uniformly.
  const normalizedMinDate = useMemo(
    () => (minDate ? startOfDay(minDate) : undefined),
    [minDate],
  );
  const normalizedMaxDate = useMemo(
    () => (maxDate ? endOfDay(maxDate) : undefined),
    [maxDate],
  );

  const disabledMatchers = useMemo(() => {
    const matchers: Array<{ dayOfWeek: number[] } | { before: Date } | { after: Date }> = [];
    if (disabledDaysOfWeek.length > 0) matchers.push({ dayOfWeek: disabledDaysOfWeek });
    if (normalizedMinDate) matchers.push({ before: normalizedMinDate });
    if (normalizedMaxDate) matchers.push({ after: normalizedMaxDate });
    return matchers.length > 0 ? matchers : undefined;
  }, [disabledDaysOfWeek, normalizedMinDate, normalizedMaxDate]);

  const isTodayDisabled = useMemo(() => {
    // Honour the `today` override so the Today button's disabled state and the
    // dinnerplate-white highlight agree on the same reference date.
    const referenceToday = today ?? new Date();
    if (disabledDaysOfWeek.includes(referenceToday.getDay())) return true;
    if (normalizedMinDate && (referenceToday < normalizedMinDate)) return true;
    if (normalizedMaxDate && (referenceToday > normalizedMaxDate)) return true;
    return false;
  }, [today, disabledDaysOfWeek, normalizedMinDate, normalizedMaxDate]);

  const handleTodayClick = useCallback(() => {
    handleSelect(startOfDay(today ?? new Date()));
  }, [handleSelect, today]);

  // Not `handleSelect` — its deselect path deliberately keeps the popover open.
  const handleClear = useCallback(() => {
    onChange?.(undefined);
    setOpen(false);
  }, [onChange, setOpen]);

  const canClear = showClear && Boolean(value);

  // Build a date-keyed label lookup so the custom DayButton can show the
  // closure tooltip without scanning the array for each rendered cell.
  const closedLabelByDate = useMemo(() => {
    if (!closedDays) return undefined;
    const map = new Map<string, string>();
    for (const { date, label } of closedDays) {
      if (label) map.set(toLocalDateKey(date), label);
    }
    return map.size > 0 ? map : undefined;
  }, [closedDays]);

  const closedDates = useMemo(
    () => closedDays?.map((c) => c.date),
    [closedDays],
  );

  return (
    // Scopes the 150ms hover delay to this calendar's closed-day tooltips.
    // Nesting inside an outer app-level TooltipProvider is fine — Radix uses the
    // nearest one, so these tooltips just adopt this delay rather than the app's.
    <TooltipProvider delayDuration={150} disableHoverableContent>
    <ClosedDayLabelsContext.Provider value={closedLabelByDate}>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <FieldTrigger
          disabled={disabled}
          aria-invalid={invalid || undefined}
          className={cn(
            'text-[15px] hover:border-dinnerplate-white',
            !displayValue && 'text-grey-light',
            invalid && 'border-chilli-red-light bg-chilli-blush hover:border-chilli-red-light',
            className,
          )}
        >
          <span>{displayValue || placeholder}</span>
          <CalendarIcon className="size-5 text-off-black" />
        </FieldTrigger>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] border-0 p-0 bg-transparent shadow-none animate-dropdown-in motion-reduce:animate-none" align="start">
        <div className="w-full border-[0.5px] border-dinnerplate-white rounded-input bg-off-white font-inter shadow-soft">
          <div className="flex flex-col gap-[18px] p-[18px]">
            <div className="flex justify-center gap-3">
              <CalendarPickerDropdown
                value={String(month.getMonth())}
                items={monthItems}
                onSelect={handleMonthChange}
                className="w-[151.5px] h-[45px]"
              />
              <CalendarPickerDropdown
                value={String(month.getFullYear())}
                items={yearItems}
                onSelect={handleYearChange}
                className="w-[151.5px] h-[45px]"
              />
            </div>

            <DayPicker
              mode="single"
              required={required}
              selected={value}
              onSelect={handleSelect}
              month={month}
              onMonthChange={setDisplayMonth}
              showOutsideDays
              hideNavigation
              weekStartsOn={CALENDAR_WEEK_STARTS_ON}
              today={today}
              disabled={disabledMatchers}
              modifiers={closedDates ? { closed: closedDates } : undefined}
              modifiersClassNames={{ closed: 'is-closed' }}
              formatters={FORMATTERS}
              classNames={calendarClassNames}
              components={DAY_COMPONENTS}
            />
            {footer}
          </div>

          <div className="border-t border-dinnerplate-white" />

          <div className={cn('flex px-[18px] py-[12px]', canClear ? 'justify-between' : 'justify-end')}>
            {canClear && (
              <Button
                variant="tertiary-light"
                aria-label={t('date_picker.clear_aria_label')}
                disabled={disabled}
                onClick={handleClear}
              >
                {t('date_picker.clear')}
              </Button>
            )}
            <Button
              variant="tertiary-light"
              aria-label={t('date_picker.today_aria_label')}
              disabled={disabled || isTodayDisabled}
              onClick={handleTodayClick}
            >
              {t('date_picker.today')}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
    </ClosedDayLabelsContext.Provider>
    </TooltipProvider>
  );
}
